import _ from 'lodash';
import { Store } from '@/core/flux.service';
import type { ExternalAgentBuilderPrompt } from '@/agentBuilder/agentBuilder.types';
import type {
  AIMemory,
  AgentType,
  AskUserAnswer,
  AskUserDraft,
  AskUserQuestion,
  ChatAttachment,
  ChatMessage,
  ExternalAIAssistantPayload,
  FontSize,
  ReasoningStyle,
  SelectedDocument,
} from '@/aiAssistant/aiAssistant.types';
import type { InitializeMode, PersistenceLevel } from '@/core/flux.service';

const AI_ASSISTANT_ACTIVE_CHAT_SESSION_STORAGE_KEY = 'sqAiAssistantStoreActiveChat';
const AI_ASSISTANT_DISPLAYED_SESSION_STORAGE_KEY = 'sqAiAssistantStoreDisplayed';
const AI_ASSISTANT_DISPLAYED_LAST_CHOICE_LOCAL_STORAGE_KEY = 'sqAiAssistantStoreDisplayedLastChoice';

function readActiveChatFromSessionStorage(): { id: string; name: string } | undefined {
  try {
    const raw =
      window.sessionStorage.getItem(AI_ASSISTANT_ACTIVE_CHAT_SESSION_STORAGE_KEY) ??
      window.sessionStorage.getItem('sqAiAssistantStore.activeChat');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return undefined;
    const parsedObject = parsed as Record<string, unknown>;
    const id = parsedObject.id;
    if (typeof id !== 'string' || !id) return undefined;
    const name = parsedObject.name;
    return { id, name: typeof name === 'string' ? name : '' };
  } catch {
    return undefined;
  }
}

function readDisplayedFromBrowserStorage(): boolean | undefined {
  try {
    const rawSessionValue =
      window.sessionStorage.getItem(AI_ASSISTANT_DISPLAYED_SESSION_STORAGE_KEY) ??
      window.sessionStorage.getItem('sqAiAssistantStore.displayed');
    if (rawSessionValue !== null) return rawSessionValue === 'true';

    const rawLastChoiceValue = window.localStorage.getItem(AI_ASSISTANT_DISPLAYED_LAST_CHOICE_LOCAL_STORAGE_KEY);
    if (rawLastChoiceValue !== null) return rawLastChoiceValue === 'true';

    return undefined;
  } catch {
    return undefined;
  }
}

interface AIAssistantState {
  displayed: boolean;
  minimized: boolean;
  maximized: boolean;
  libraryDisplayed: boolean;
  libraryEditorDisplayed: boolean;
  startNewChatOnOpen: boolean;
  messages: ChatMessage[];
  selectedAgent: AgentType | undefined;
  fontSize: FontSize;
  externalPrompt: ExternalAIAssistantPayload | undefined;
  externalAgentBuilderPrompt: ExternalAgentBuilderPrompt | undefined;
  reasoningStyle: ReasoningStyle;
  refreshHistory: boolean;
  activeChat: { id: string; name: string } | undefined;
  historySearchValue: string;
  selectedDocument: SelectedDocument | SelectedDocument[] | null;
  generateMemory: boolean;
  savedMemories: AIMemory[];
  preferredSubsection: 'general' | 'memories' | 'privacy' | null;
  draftAttachments: ChatAttachment[];
  editingLibraryItemId: string | null;
  askUserQuestions: Record<string, AskUserQuestion>;
  askUserDrafts: Record<string, AskUserDraft>;
  askUserPanelCollapsed: boolean;
}

type AIAssistantDehydratedState = Omit<
  AIAssistantState,
  | 'messages'
  | 'externalPrompt'
  | 'externalAgentBuilderPrompt'
  | 'historySearchValue'
  | 'displayed'
  | 'activeChat'
  | 'selectedDocument'
  | 'preferredSubsection'
  | 'draftAttachments'
>;

type LegacyAIAssistantDehydratedState = Partial<AIAssistantDehydratedState> & {
  activeChat?: { id: string; name: string };
  displayed?: boolean;
};

export class AiAssistantStore extends Store {
  static readonly storeName = 'sqAiAssistantStore';
  persistenceLevel: PersistenceLevel = 'WORKBENCH';

  initialize(initializeMode: InitializeMode): void {
    const initialState: AIAssistantState = {
      displayed: readDisplayedFromBrowserStorage() ?? false,
      minimized: false,
      libraryEditorDisplayed: false,
      maximized: false,
      libraryDisplayed: false,
      startNewChatOnOpen: false,
      messages: [],
      selectedAgent: undefined,
      fontSize: 'small',
      reasoningStyle: 'enabled',
      refreshHistory: true,
      activeChat: readActiveChatFromSessionStorage(),
      historySearchValue: '',
      selectedDocument: null,
      generateMemory: true,
      savedMemories: [],
      preferredSubsection: null,
      draftAttachments: [],
      editingLibraryItemId: null,
      externalPrompt: undefined,
      externalAgentBuilderPrompt: undefined,
      askUserQuestions: {},
      askUserDrafts: {},
      askUserPanelCollapsed: false,
    };
    this.state = this.immutable(initialState);
  }

  get displayed(): boolean {
    return this.state.get('displayed');
  }

  get minimized(): boolean {
    return this.state.get('minimized');
  }

  get maximized(): boolean {
    return this.state.get('maximized');
  }

  get libraryDisplayed(): boolean {
    return this.state.get('libraryDisplayed');
  }

  get libraryEditorDisplayed(): boolean {
    return this.state.get('libraryEditorDisplayed');
  }

  get startNewChatOnOpen(): boolean {
    return this.state.get('startNewChatOnOpen');
  }

  get messages(): ChatMessage[] {
    return this.state.get('messages') || [];
  }

  // NOTE: 'vantage' and 'workbench' are legacy agents that are no longer selectable but may still be set on existing chats
  get selectedAgent(): AgentType | 'vantage' | 'workbench' | undefined {
    return this.state.get('selectedAgent');
  }

  get fontSize(): FontSize {
    return this.state.get('fontSize') || 'small';
  }

  get externalPrompt(): ExternalAIAssistantPayload | undefined {
    return this.state.get('externalPrompt');
  }

  get externalAgentBuilderPrompt(): ExternalAgentBuilderPrompt | undefined {
    return this.state.get('externalAgentBuilderPrompt');
  }

  get reasoningStyle(): ReasoningStyle {
    return this.state.get('reasoningStyle');
  }

  get refreshHistory(): boolean {
    return this.state.get('refreshHistory');
  }

  get activeChat(): { id: string; name: string } | undefined {
    return this.state.get('activeChat');
  }

  get historySearchValue(): string {
    return this.state.get('historySearchValue') || '';
  }

  get selectedDocument(): SelectedDocument | SelectedDocument[] | null {
    return this.state.get('selectedDocument');
  }

  get generateMemory(): boolean {
    return this.state.get('generateMemory');
  }

  get savedMemories(): AIMemory[] {
    return this.state.get('savedMemories') || [];
  }

  get preferredSubsection(): 'general' | 'memories' | 'privacy' | null {
    return this.state.get('preferredSubsection');
  }

  get draftAttachments(): ChatAttachment[] {
    return this.state.get('draftAttachments') || [];
  }

  get editingLibraryItemId(): string | null {
    return this.state.get('editingLibraryItemId');
  }

  get askUserQuestions(): Record<string, AskUserQuestion> {
    return this.state.get('askUserQuestions') || {};
  }

  get askUserDrafts(): Record<string, AskUserDraft> {
    return this.state.get('askUserDrafts') || {};
  }

  get askUserPanelCollapsed(): boolean {
    return Boolean(this.state.get('askUserPanelCollapsed'));
  }

  /**
   * Returns pending ask_user questions for the given chat, sorted by createdAt.
   */
  getPendingAskUserQuestions(chatId: string | undefined): AskUserQuestion[] {
    if (!chatId) return [];
    const questions = this.askUserQuestions;
    return Object.values(questions)
      .filter((q) => q.chatId === chatId && q.status === 'pending')
      .sort((a, b) => a.createdAt - b.createdAt);
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate(): AIAssistantDehydratedState {
    return _.omit(this.state.serialize(), [
      'messages',
      'externalPrompt',
      'externalAgentBuilderPrompt',
      'historySearchValue',
      'displayed',
      'activeChat',
      'selectedDocument',
      'preferredSubsection',
      'draftAttachments',
      'startNewChatOnOpen',
      'minimized',
    ]);
  }

  /**
   * Re-creates the AI Store. All necessary data needed to rehydrate is persisted so no actions have to be
   * called.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<AIAssistantDehydratedState>): void {
    // Ignore legacy/persisted fields that are now managed outside WORKBENCH persistence:
    // - activeChat (tab-local sessionStorage)
    // - displayed (sessionStorage with localStorage fallback for last explicit choice)
    const sanitized = _.omit(dehydratedState as LegacyAIAssistantDehydratedState, [
      'activeChat',
      'displayed',
    ]) as Partial<AIAssistantDehydratedState>;
    this.state.merge(sanitized);
  }

  protected readonly handlers = {
    /**
     * Sets the displayed flag
     */
    AI_ASSISTANT_DISPLAYED: (payload: { displayed: boolean }) => {
      this.state.set('displayed', payload.displayed);
    },

    /**
     * Sets the maximized flag
     */
    AI_ASSISTANT_MAXIMIZED: (payload: { maximized: boolean }) => {
      this.state.set('maximized', payload.maximized);
    },

    AI_ASSISTANT_LIBRARY_DISPLAYED: (payload: { libraryDisplayed: boolean }) => {
      this.state.set('libraryDisplayed', payload.libraryDisplayed);
    },

    AI_ASSISTANT_SET_MINIMIZED: (payload: { minimized: boolean }) => {
      this.state.set('minimized', payload.minimized);
    },

    AI_ASSISTANT_SET_START_NEW_CHAT_ON_OPEN: (payload: { startNewChatOnOpen: boolean }) => {
      this.state.set('startNewChatOnOpen', payload.startNewChatOnOpen);
    },

    AI_ASSISTANT_SET_MESSAGES: (payload: { messages: ChatMessage[] }) => {
      this.state.set('messages', payload.messages);
    },

    AI_ASSISTANT_SET_SELECTED_AGENT: (payload: { agent: AgentType }) => {
      this.state.set('selectedAgent', payload.agent);
    },

    AI_ASSISTANT_SET_FONT_SIZE: (payload: { fontSize: FontSize }) => {
      this.state.set('fontSize', payload.fontSize);
    },

    AI_ASSISTANT_EXTERNAL_PROMPT: (payload: ExternalAIAssistantPayload | undefined) => {
      this.state.set('externalPrompt', payload);
    },

    AI_ASSISTANT_EXTERNAL_AGENT_BUILDER_PROMPT: (payload: ExternalAgentBuilderPrompt | undefined) => {
      this.state.set('externalAgentBuilderPrompt', payload);
    },

    AI_ASSISTANT_SET_REASONING_STYLE: (payload: { reasoningStyle: ReasoningStyle }) => {
      this.state.set('reasoningStyle', payload.reasoningStyle);
    },

    AI_ASSISTANT_SET_REFRESH_HISTORY: (payload: { refreshHistory: boolean }) => {
      this.state.set('refreshHistory', payload.refreshHistory);
    },

    AI_ASSISTANT_SET_ACTIVE_CHAT: (payload: { chat: { id: string; name: string } | undefined }) => {
      this.state.set('activeChat', payload.chat);
    },

    AI_ASSISTANT_SET_HISTORY_SEARCH: (payload: { searchValue: string }) => {
      this.state.set('historySearchValue', payload.searchValue);
    },

    AI_ASSISTANT_SET_SELECTED_DOCUMENT: (payload: { document: SelectedDocument | SelectedDocument[] | null }) => {
      this.state.set('selectedDocument', payload.document);
    },

    AI_ASSISTANT_SET_GENERATE_MEMORY: (payload: { generateMemory: boolean }) => {
      this.state.set('generateMemory', payload.generateMemory);
    },

    AI_ASSISTANT_SET_SAVED_MEMORIES: (payload: { savedMemories: AIMemory[] }) => {
      this.state.set('savedMemories', payload.savedMemories);
    },

    AI_ASSISTANT_SET_PREFERRED_SUBSECTION: (payload: { subsection: 'general' | 'memories' | 'privacy' | null }) => {
      this.state.set('preferredSubsection', payload.subsection);
    },

    AI_ASSISTANT_ADD_MEMORY: (payload: { memory: AIMemory }) => {
      const currentMemories = this.state.get('savedMemories') || [];
      this.state.set('savedMemories', [...currentMemories, payload.memory]);
    },

    AI_ASSISTANT_REMOVE_MEMORY: (payload: { memoryId: string }) => {
      const currentMemories = this.state.get('savedMemories') || [];
      const filteredMemories = currentMemories.filter((memory: AIMemory) => memory.id !== payload.memoryId);
      this.state.set('savedMemories', filteredMemories);
    },

    AI_ASSISTANT_UPDATE_MEMORY: (payload: { memory: AIMemory }) => {
      const currentMemories = this.state.get('savedMemories') || [];
      const updatedMemories = currentMemories.map((memory: AIMemory) =>
        memory.id === payload.memory.id ? payload.memory : memory,
      );
      this.state.set('savedMemories', updatedMemories);
    },

    AI_ASSISTANT_ADD_DRAFT_ATTACHMENT: (payload: { attachment: ChatAttachment }) => {
      const currentAttachments = this.state.get('draftAttachments') || [];
      this.state.set('draftAttachments', [...currentAttachments, payload.attachment]);
    },

    AI_ASSISTANT_REMOVE_DRAFT_ATTACHMENT: (payload: { attachmentId: string }) => {
      const currentAttachments = this.state.get('draftAttachments') || [];
      const filteredAttachments = currentAttachments.filter(
        (attachment: ChatAttachment) => attachment.id !== payload.attachmentId,
      );
      this.state.set('draftAttachments', filteredAttachments);
    },

    AI_ASSISTANT_CLEAR_DRAFT_ATTACHMENTS: () => {
      this.state.set('draftAttachments', []);
    },

    AI_ASSISTANT_SET_LIBRARY_EDITOR_STATE: (payload: { id: string | null; libraryEditorDisplayed: boolean }) => {
      this.state.set('editingLibraryItemId', payload.id);
      this.state.set('libraryEditorDisplayed', payload.libraryEditorDisplayed);
    },

    AI_ASSISTANT_ADD_ASK_USER_QUESTION: (payload: { question: AskUserQuestion }) => {
      const current = this.state.get('askUserQuestions') || {};
      if (current[payload.question.id]) return;
      this.state.set('askUserQuestions', { ...current, [payload.question.id]: payload.question });
    },

    AI_ASSISTANT_UPDATE_ASK_USER_DRAFT: (payload: { questionId: string; draft: AskUserDraft; replace?: boolean }) => {
      const drafts = this.state.get('askUserDrafts') || {};
      const previous = drafts[payload.questionId] || {};
      // When replace=true, the supplied draft fully replaces any previous draft for the question
      // (used to support deselection by writing {letter: undefined, otherText: undefined}).
      const next = payload.replace ? payload.draft : { ...previous, ...payload.draft };
      this.state.set('askUserDrafts', { ...drafts, [payload.questionId]: next });
    },

    AI_ASSISTANT_RESOLVE_ASK_USER_QUESTION: (payload: { questionId: string; answer: AskUserAnswer }) => {
      const current = this.state.get('askUserQuestions') || {};
      const existing = current[payload.questionId];
      if (!existing) return;
      const updated: AskUserQuestion = {
        ...existing,
        status: payload.answer.kind === 'skipped' ? 'skipped' : 'answered',
        answer: payload.answer,
      };
      this.state.set('askUserQuestions', { ...current, [payload.questionId]: updated });
      const drafts = this.state.get('askUserDrafts') || {};
      if (drafts[payload.questionId]) {
        const rest = { ...drafts };
        delete rest[payload.questionId];
        this.state.set('askUserDrafts', rest);
      }
    },

    AI_ASSISTANT_CANCEL_ASK_USER_FOR_CHAT: (payload: { chatId: string }) => {
      const current = (this.state.get('askUserQuestions') as Record<string, AskUserQuestion>) || {};
      const remaining = Object.fromEntries(
        (Object.entries(current) as [string, AskUserQuestion][]).filter(
          ([, q]) => !(q.chatId === payload.chatId && q.status === 'pending'),
        ),
      );
      this.state.set('askUserQuestions', remaining);
      const drafts = (this.state.get('askUserDrafts') as Record<string, AskUserDraft>) || {};
      const remainingDrafts = Object.fromEntries(
        (Object.entries(drafts) as [string, AskUserDraft][]).filter(
          ([id]) => !(current[id] && current[id].chatId === payload.chatId),
        ),
      );
      this.state.set('askUserDrafts', remainingDrafts);
    },

    AI_ASSISTANT_SET_ASK_USER_PANEL_COLLAPSED: (payload: { collapsed: boolean }) => {
      this.state.set('askUserPanelCollapsed', payload.collapsed);
    },
  };
}
